import { faCloudExclamation } from '@fortawesome/pro-light-svg-icons';
import { faCodeBranch, faCubes } from '@fortawesome/pro-thin-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ConfigurationProvider } from '@partful/features/configurations/lib/configurationProvider';
import { useConfiguration } from '@partful/features/configurations/lib/useConfiguration';
import { Configuration } from '@partful/features/configurations/model/types';
import { ErrorPage } from '@partful/features/instructions/ui/ErrorPage';
import { LoadingPage } from '@partful/features/instructions/ui/LoadingPage';
import { Button } from '@partful/shared/ui/Button';
import { PageBrandedPanel } from '@partful/shared/ui/PageBrandedPanel';
import { MobilePageNavigationContent, PageNavigation } from '@partful/shared/ui/PageNavigation';
import { StandardPageHeader, StandardPageLayout, StandardPageMain } from '@partful/shared/ui/StandardPageLayout';
import { Typography } from '@partful/shared/ui/Typography';
import { useTenant } from '@providers/Tenant';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Redirect, Route, Switch, useLocation, useParams } from 'react-router-dom';
import styles from './ManageInstructionsPage.module.scss';
import { OverviewPage } from './overview';
import { VersionsPageRouter } from './versions';

type NavRoute = { label: ReactNode; path: string; icon: ReactNode; enabled?: boolean };

export function ManageInstructionsPage() {
  const { t: i18n } = useTranslation();
  const { enabledFeatures } = useTenant();
  const { configurationId } = useParams<{ configurationId: string }>();
  const { pathname } = useLocation();

  const { data: configuration, isLoading, isError } = useConfiguration(configurationId);

  const navRoutes: NavRoute[] = [
    {
      label: i18n('instructions:manage.overview.title'),
      path: `/instructions/manage/${configurationId}/overview?productName=${
        configuration ? encodeURIComponent(configuration.name) : ''
      }`,
      icon: <FontAwesomeIcon icon={faCubes} fixedWidth />,
    },
    {
      label: i18n('instructions:manage.versions.title'),
      path: `/instructions/manage/${configurationId}/versions?productName=${
        configuration ? encodeURIComponent(configuration.name) : ''
      }`,
      icon: <FontAwesomeIcon icon={faCodeBranch} fixedWidth />,
      enabled: enabledFeatures.includes('instructions'),
    },
  ];
  const activeRoute = navRoutes.find(({ path }) => pathname.startsWith(path));

  return (
    <StandardPageLayout>
      <StandardPageHeader>
        <PageBrandedPanel>
          {configuration && (
            <>
              <Nav configuration={configuration} routes={navRoutes} activeRoute={activeRoute} />
              <MobilePageNavigationContent
                render={({ close }) => (
                  <Nav
                    configuration={configuration}
                    routes={navRoutes}
                    activeRoute={activeRoute}
                    onRouteClick={/* istanbul ignore next - better tested e2e */ () => close()}
                  />
                )}
              />
            </>
          )}
        </PageBrandedPanel>
        <PageNavigation />
      </StandardPageHeader>
      <StandardPageMain>
        {isLoading && <LoadingPage />}
        {isError && (
          <ErrorPage
            icon={<FontAwesomeIcon icon={faCloudExclamation} />}
            title={i18n('instructions:manage.loadFailedError.title')}
            message={i18n('instructions:manage.loadFailedError.message')}
            actions={
              <Button linkComponent={Link} to="/instructions">
                {i18n('instructions:manage.loadFailedError.backToLibrary')}
              </Button>
            }
          />
        )}
        {configuration && (
          <ConfigurationProvider configuration={configuration}>
            <Switch>
              <Route exact path="/instructions/manage/:configurationId/overview">
                <OverviewPage />
              </Route>
              <Route path="/instructions/manage/:configurationId/versions">
                <VersionsPageRouter />
              </Route>
              <Route
                path="/instructions/manage/:configurationId"
                render={({ match }) => (
                  <Redirect
                    to={`/instructions/manage/${match.params.configurationId}/overview?productName=${encodeURIComponent(
                      configuration.name
                    )}`}
                  />
                )}
              />
            </Switch>
          </ConfigurationProvider>
        )}
      </StandardPageMain>
    </StandardPageLayout>
  );
}

type NavProps = {
  configuration: Configuration;
  routes: NavRoute[];
  activeRoute?: NavRoute;
  onRouteClick?: () => void;
};

function Nav({ configuration, routes, activeRoute, onRouteClick }: Readonly<NavProps>) {
  return (
    <div className={styles.manageSideBar}>
      <div className={styles.title}>
        <Typography appearance="heading6">{configuration?.name}</Typography>
      </div>
      <nav className={styles.nav}>
        {routes.map(
          ({ enabled, label, path, icon }) =>
            enabled !== false && (
              <NavLink
                key={path}
                to={path}
                aria-current={/* istanbul ignore next */ path === activeRoute?.path ? 'page' : undefined}
                onClick={onRouteClick}
              >
                {icon}
                <span>{label}</span>
              </NavLink>
            )
        )}
      </nav>
    </div>
  );
}
